import { message } from "antd";
import dayjs from "dayjs";
import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { b2bErrorCode, b2bErrorText, onB2BError } from "@/utils/b2bErrors";
/**
 * Взаиморасчёты компании — вкладка карточки (UC1120, WP-DSP-2 P-7). Только ADMINISTRATOR (D14).
 * Идемпотентность ключа (`uuid()`) живёт от открытия формы до успешного ответа (§1.7).
 */
export class CompanySettlementsStore {
    constructor(settlementsService, singleCompanyStore) {
        this.settlementsService = settlementsService;
        this.singleCompanyStore = singleCompanyStore;
        this.companyId = null;
        this.invoices = [];
        this.payments = [];
        this.ledger = [];
        this.lines = {};
        this.isLoading = false;
        this.isSubmitting = false;
        this.lastRunResult = null;
        this.formError = null;
        this.paymentIdempotencyKey = null;
        this.adjustmentIdempotencyKey = null;
        this.generation = 0;
        makeAutoObservable(this, {
            settlementsService: false,
            singleCompanyStore: false,
            generation: false,
        });
    }
    async init(companyId) {
        this.dispose();
        this.companyId = companyId;
        await this.load();
    }
    async load() {
        const companyId = this.companyId;
        if (!companyId)
            return;
        const generation = this.generation;
        this.isLoading = true;
        // Строки счетов перечитываются после каждой загрузки (аннулирование/перевыставление меняют их состав).
        this.lines = {};
        try {
            const [invoices, payments, ledger] = await Promise.all([
                this.settlementsService.listInvoices(companyId),
                this.settlementsService.listPayments(companyId),
                this.settlementsService.listLedger(companyId),
            ]);
            // Шапка (v_corp_balances) и карточка (v_corp_companies → credit_state): бейджи лимита без reload (WP-DSP-3-FIX)
            await Promise.all([this.singleCompanyStore.loadFinance(), this.singleCompanyStore.refreshCompanyQuietly()]);
            runInAction(() => {
                if (generation !== this.generation)
                    return;
                this.invoices = invoices;
                this.payments = payments;
                this.ledger = ledger;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить взаиморасчёты компании");
        }
        finally {
            runInAction(() => {
                if (generation === this.generation)
                    this.isLoading = false;
            });
        }
    }
    /** Строки счёта (`corp-invoices-table` → drawer/детали, §10.2). */
    async openInvoice(invoiceId) {
        if (this.lines[invoiceId])
            return;
        try {
            const lines = await this.settlementsService.listInvoiceLines(invoiceId);
            runInAction(() => {
                this.lines[invoiceId] = lines;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить строки счёта");
        }
    }
    /** Ключ идемпотентности создаётся при открытии формы оплаты и живёт до успешного ответа (§1.7). */
    openPaymentForm() {
        this.paymentIdempotencyKey = uuidv4();
        this.formError = null;
    }
    openAdjustmentForm() {
        this.adjustmentIdempotencyKey = uuidv4();
        this.formError = null;
    }
    clearFormError() {
        this.formError = null;
    }
    async registerPayment(input) {
        const companyId = this.companyId;
        if (!companyId)
            return false;
        const key = this.paymentIdempotencyKey ?? uuidv4();
        this.paymentIdempotencyKey = key;
        return this.submit(() => this.settlementsService.registerPayment(companyId, input, key), "Оплата зарегистрирована", "Не удалось зарегистрировать оплату", "payment", () => {
            this.paymentIdempotencyKey = null;
        });
    }
    async voidPayment(paymentId, reason) {
        return this.submit(() => this.settlementsService.voidPayment(paymentId, reason), "Оплата сторнирована", "Не удалось сторнировать оплату", "payment");
    }
    async createAdjustment(input) {
        const companyId = this.companyId;
        if (!companyId)
            return false;
        const key = this.adjustmentIdempotencyKey ?? uuidv4();
        this.adjustmentIdempotencyKey = key;
        return this.submit(() => this.settlementsService.createAdjustment(companyId, input, key), "Корректировка создана", "Не удалось создать корректировку", "settlements", () => {
            this.adjustmentIdempotencyKey = null;
        });
    }
    /** `corp-generate-invoices` (§10.2); `asOf` — ISO timestamptz из `corp-as-of`. */
    async generateInvoices(asOf) {
        const companyId = this.companyId;
        if (!companyId)
            return false;
        this.isSubmitting = true;
        this.formError = null;
        try {
            const result = await this.settlementsService.generateInvoices(companyId, asOf);
            runInAction(() => {
                this.lastRunResult = result;
            });
            await this.load();
            return true;
        }
        catch (error) {
            runInAction(() => {
                const code = b2bErrorCode(error);
                // 42501 при дате в будущем — на PROD `allow_future_as_of` выключен намеренно (WP-DSP-3-FIX)
                const futureAsOf = code === "42501" && dayjs(asOf).isAfter(dayjs(), "day");
                this.formError = {
                    code,
                    text: futureAsOf
                        ? "Дата «по состоянию на» в будущем запрещена настройками — укажите сегодняшнюю или прошедшую дату"
                        : b2bErrorText(error, "Не удалось сформировать счета", "invoice"),
                };
            });
            return false;
        }
        finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
    async voidInvoice(invoiceId, reason) {
        return this.submit(() => this.settlementsService.voidInvoice(invoiceId, reason), "Счёт аннулирован", "Не удалось аннулировать счёт", "invoice");
    }
    async setExternalDoc(invoiceId, input) {
        return this.submit(() => this.settlementsService.setExternalDoc(invoiceId, input), "Реквизиты документа сохранены", "Не удалось сохранить реквизиты документа", "invoice");
    }
    async submit(action, successText, failFallback, context, onSuccessCleanup) {
        this.isSubmitting = true;
        this.formError = null;
        try {
            await action();
            onSuccessCleanup?.();
            message.success(successText);
            await this.load();
            return true;
        }
        catch (error) {
            runInAction(() => {
                this.formError = { code: b2bErrorCode(error), text: b2bErrorText(error, failFallback, context) };
            });
            return false;
        }
        finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
    dispose() {
        this.generation += 1;
        this.companyId = null;
        this.invoices = [];
        this.payments = [];
        this.ledger = [];
        this.lines = {};
        this.isLoading = false;
        this.isSubmitting = false;
        this.lastRunResult = null;
        this.formError = null;
        this.paymentIdempotencyKey = null;
        this.adjustmentIdempotencyKey = null;
    }
}
